import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material";
import { useEffect, useState } from "react";

const initialForm = {
  code: "",
  name: "",
  type: "E",
  paymentType: "M",
  defaultAmount: 0,
  defaultPercentage: 0,
  taxable: true,
  isEpf: false,
  isEtf: false,
  isOT: false,
  isPayee: false,
  isNoPay: false,
  calculationMethod: "F",
  remarks: "",
};

const componentTypes = [
  { value: "E", label: "Earning" },
  { value: "D", label: "Deduction" },
  { value: "C", label: "Contribution" },
];

const calculationMethods = [
  { value: "F", label: "Fixed" },
  { value: "P", label: "Daily-Based" },
];

const paymentTypes = [
  { value: "M", label: "Monthly" },
  { value: "W", label: "Weekly" },
];

const inputClass = `
  w-full h-14
  rounded-md
  border border-surface-200
  bg-white
  px-3.5
  text-[16px] text-surface-800
  outline-none
  transition-colors
  focus:border-primary-500
  focus:ring-1 focus:ring-primary-500
  disabled:bg-gray-100 disabled:text-gray-700 disabled:cursor-not-allowed
`;

const errorClass = '!border-red-400 focus:!border-red-500 focus:!ring-red-500';

const labelClass = `
  absolute
  left-3
  top-0
  -translate-y-1/2
  bg-white
  px-1.5
  text-[13px]
  text-surface-500
  pointer-events-none
  z-10
`;

const maskCurrencyInput = (rawValue) => {
  const digits = String(rawValue ?? "").replace(/\D/g, "");
  if (!digits) return "";
  const padded = digits.padStart(3, "0");
  const cents = padded.slice(-2);
  const wholeDigits = padded.slice(0, -2).replace(/^0+(?=\d)/, "");
  const whole = Number(wholeDigits || "0").toLocaleString("en-US");
  return `${whole}.${cents}`;
};

const parseCurrency = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

function Field({ label, children, error = "" }) {
  return (
    <div className="relative">
      <label className={labelClass}>{label}</label>
      {children}
      {error && (
        <p className="mt-1.5 flex items-start gap-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  type = "text",
  min,
  step,
  disabled = false,
  error = "",
}) {
  return (
    <Field label={label} error={error}>
      <input
        type={type}
        value={value}
        onChange={onChange}
        min={min}
        step={step}
        disabled={disabled}
        className={`${inputClass} ${error ? errorClass : ""}`}
      />
    </Field>
  );
}

function SelectInput({ label, value, onChange, children, disabled = false, error = "" }) {
  return (
    <Field label={label} error={error}>
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={`${inputClass} appearance-auto cursor-pointer ${
          error ? errorClass : ""
        }`}
      >
        {children}
      </select>
    </Field>
  );
}

function SwitchInput({ label, checked, onChange, disabled = false }) {
  return (
    <div className={`flex h-14 items-center rounded-md border border-surface-200 bg-white px-3.5 ${disabled ? 'bg-gray-100 opacity-90' : ''}`}>
      <label className={`flex items-center gap-3 text-[15px] text-surface-800 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="h-5 w-5 cursor-pointer rounded border-surface-300 accent-primary-600 disabled:cursor-not-allowed"
        />
        <span>{label}</span>
      </label>
    </div>
  );
}

function CurrencyInput({
  label,
  value,
  onChange,
  disabled = false,
  error = "",
}) {
  return (
    <Field label={label} error={error}>
      <input
        type="text"
        inputMode="decimal"
        value={value ?? ""}
        disabled={disabled}
        onChange={(event) => {
          const maskedValue = maskCurrencyInput(event.target.value);
          onChange({
            target: {
              type: "text",
              value: maskedValue,
            },
          });
        }}
        className={`${inputClass} ${error ? errorClass : ""}`}
      />
    </Field>
  );
}

export default function AddPayrollComponentDialog({
  open,
  onClose,
  onSave,
  loading = false,
  mode = "add", // "add" | "edit" | "view"
  component = null,
}) {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  const isView = mode === "view";
  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open) return;

    if ((isEdit || isView) && component) {
      // Map entity model values (e.g., Earning -> E, Fixed -> F)
      const mappedType =
        component.type === "Earning" || component.type === "E"
          ? "E"
          : component.type === "Deduction" || component.type === "D"
          ? "D"
          : component.type === "Contribution" || component.type === "C"
          ? "C"
          : component.type || "E";

      const mappedCalc =
        component.calcMethod === "Fixed" || component.calcMethod === "F"
          ? "F"
          : component.calcMethod === "Daily-Based" || component.calcMethod === "Percentage" || component.calcMethod === "P"
          ? "P"
          : component.calcMethod || "F";

      setForm({
        id: component.id,
        code: component.code || "",
        name: component.name || "",
        type: mappedType,
        paymentType: component.paymentType || "M",
        defaultAmount: maskCurrencyInput(component.defaultAmount || 0),
        defaultPercentage: component.defaultPercentage ?? 0,
        taxable: Boolean(component.taxable),
        isEpf: Boolean(component.epf ?? component.isEpf),
        isEtf: Boolean(component.etf ?? component.isEtf),
        isOT: Boolean(component.otPayable ?? component.isOT),
        isPayee: Boolean(component.isPayee),
        isNoPay: Boolean(component.isNoPay),
        calculationMethod: mappedCalc,
        remarks: component.remarks || "",
      });
      setErrors({});
    } else {
      setForm(initialForm);
      setErrors({});
    }
  }, [open, mode, component, isEdit, isView]);

  const handleChange = (field) => (e) => {
    if (isView) return;

    const value =
      e.target.type === "checkbox"
        ? e.target.checked
        : e.target.value;

    setForm((prev) => {
      if (field === "calculationMethod") {
        return {
          ...prev,
          calculationMethod: value,
          defaultAmount: value === "F" ? prev.defaultAmount : 0,
          defaultPercentage: value === "P" ? prev.defaultPercentage : 0,
        };
      }

      return {
        ...prev,
        [field]: value,
      };
    });

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.code.trim()) {
      newErrors.code = "Component code is required.";
    } else if (form.code.trim().length > 5) {
      newErrors.code = "Component code cannot be longer than 5 characters.";
    }

    if (!form.name.trim()) {
      newErrors.name = "Component name is required.";
    }

    if (form.calculationMethod === "F" && parseCurrency(form.defaultAmount) < 0) {
      newErrors.defaultAmount = "Default amount cannot be negative.";
    }

    if (form.calculationMethod === "P") {
      if (Number(form.defaultPercentage) <= 0) {
        newErrors.defaultPercentage = "Default percentage cannot be 0 or negative.";
      } else if (Number(form.defaultPercentage) > 100) {
        newErrors.defaultPercentage = "Default percentage cannot be more than 100.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (isView || !validate()) {
      return;
    }

    onSave({
      ...(isEdit && { id: form.id || component?.id }),
      code: form.code,
      name: form.name,
      type: form.type,
      paymentType: form.paymentType,
      calculationMethod: form.calculationMethod,
      taxable: form.taxable,
      isEpf: form.isEpf,
      isEtf: form.isEtf,
      isOT: form.isOT,
      isNoPay: form.isNoPay,
      remarks: form.remarks,
      defaultAmount: form.calculationMethod === "F" ? parseCurrency(form.defaultAmount) : null,
      defaultPercentage: form.calculationMethod === "P" ? Number(form.defaultPercentage || 0) : null,
    });
  };

  const getTitle = () => {
    if (isView) return "View Payroll Component";
    if (isEdit) return "Edit Payroll Component";
    return "New Payroll Component";
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle className="!px-6 !pt-5 !pb-3">
        <span className="text-[21px] font-medium text-surface-900">
          {getTitle()}
        </span>
      </DialogTitle>

      <DialogContent className="!px-6 !py-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

          {/* Component Code */}
          <TextInput
            label="Component Code"
            value={form.code}
            disabled={isView || isEdit}
            onChange={handleChange("code")}
            error={errors.code}
          />

          {/* Component Name */}
          <TextInput
            label="Component Name"
            value={form.name}
            disabled={isView}
            error={errors.name}
            onChange={handleChange("name")}
          />

          {/* Component Type */}
          <SelectInput
            label="Component Type"
            value={form.type}
            disabled={isView}
            onChange={handleChange("type")}
          >
            {componentTypes.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </SelectInput>

          {/* Payment Type */}
          <SelectInput
            label="Payment Type"
            value={form.paymentType}
            disabled={isView}
            onChange={handleChange("paymentType")}
          >
            {paymentTypes.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </SelectInput>

          {/* Calculation Method */}
          <SelectInput
            label="Calculation Method"
            value={form.calculationMethod}
            disabled={isView}
            onChange={handleChange("calculationMethod")}
          >
            {calculationMethods.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </SelectInput>

          {/* Default Amount */}
          {form.calculationMethod === "F" && (
            <CurrencyInput
              label="Default Amount"
              value={form.defaultAmount}
              disabled={isView}
              error={errors.defaultAmount}
              onChange={handleChange("defaultAmount")}
            />
          )}

          {/* Default Percentage */}
          {form.calculationMethod === "P" && (
            <TextInput
              label="Default Percentage"
              type="number"
              min="0"
              step="0.01"
              value={form.defaultPercentage ?? ""}
              disabled={isView}
              error={errors.defaultPercentage}
              onChange={handleChange("defaultPercentage")}
            />
          )}

          {/* Taxable, EPF, and Payee Checkboxes */}
          <div className="grid grid-cols-1 gap-3 sm:col-span-2 sm:grid-cols-5">
            <SwitchInput
              label="Payee"
              checked={form.taxable}
              disabled={isView}
              onChange={handleChange("taxable")}
            />

            <SwitchInput
              label="EPF"
              checked={form.isEpf}
              disabled={isView}
              onChange={handleChange("isEpf")}
            />

            <SwitchInput
              label="ETF"
              checked={form.isEtf}
              disabled={isView}
              onChange={handleChange("isEtf")}
            />

            <SwitchInput
              label="OT"
              checked={form.isOT}
              disabled={isView}
              onChange={handleChange("isOT")}
            />

            <SwitchInput
              label="No Pay"
              checked={form.isNoPay}
              disabled={isView}
              onChange={handleChange("isNoPay")}
            />
          </div>

          {/* Remarks */}
          <div className="relative sm:col-span-2">
            <label className={labelClass}>Remarks</label>
            <textarea
              value={form.remarks}
              disabled={isView}
              onChange={handleChange("remarks")}
              rows={3}
              className={`
                w-full
                rounded-md
                border border-surface-200
                bg-white
                px-3.5
                py-3
                text-[16px] text-surface-800
                outline-none
                transition-colors
                focus:border-primary-500
                focus:ring-1 focus:ring-primary-500
                disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed
              `}
            />
          </div>

        </div>
      </DialogContent>

      <DialogActions className="!px-6 !py-3">
        <Button
          onClick={onClose}
          disabled={loading}
          className="!text-primary-600"
        >
          {isView ? "CLOSE" : "CANCEL"}
        </Button>

        {!isView && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            className="!bg-primary-600 hover:!bg-primary-700"
          >
            {loading ? "SAVING..." : isEdit ? "UPDATE" : "SAVE"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}